import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const UNSUBSCRIBE_REASONS = [
  'Too expensive',
  'Not enough clients',
  'Missing features',
  'Difficult to use',
  'Switching to another platform',
  'Temporary closure',
  'Other'
];

const UnsubscribeModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const { isDark } = useTheme();
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setError('');
  };

  const handleNext = () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }
    setStep(2);
  };

  const handleConfirm = () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }
    onConfirm({ reason: selectedReason, feedback });
  };

  const handleClose = () => {
    if (!loading) {
      setStep(1);
      setSelectedReason('');
      setFeedback('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={handleClose}
    >
      <div
        className={`relative w-full max-w-lg rounded-2xl shadow-2xl border ${
          isDark ? 'bg-zinc-900 border-white/10' : 'bg-white border-zinc-200'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        {!loading && (
          <button
            onClick={handleClose}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-zinc-100'
            }`}
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {step === 1 && (
          <div className="p-6">
            {/* Warning Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                  Cancel Subscription?
                </h2>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  We're sorry to see you go. Please help us improve by telling us why.
                </p>
              </div>
            </div>

            {/* What will happen */}
            <div className={`rounded-lg p-4 mb-6 ${isDark ? 'bg-zinc-800/50' : 'bg-zinc-50'}`}>
              <h3 className={`font-semibold mb-3 text-sm ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                What happens when you cancel:
              </h3>
              <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>Your public booking link will be disabled</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-0.5">•</span>
                  <span>Admin panel access will be restricted</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                  <span>Your data will be retained for 30 days</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 dark:text-green-400 mt-0.5">✓</span>
                  <span>You can reactivate anytime</span>
                </li>
              </ul>
            </div>

            {/* Reason selection */}
            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-3 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                Why are you cancelling? <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {UNSUBSCRIBE_REASONS.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border-2 ${
                      selectedReason === reason
                        ? isDark
                          ? 'border-[#7d6633] bg-[#7d6633]/10'
                          : 'border-[#7d6633] bg-[#7d6633]/5'
                        : isDark
                        ? 'border-white/10 hover:border-white/20 bg-zinc-800/30'
                        : 'border-zinc-200 hover:border-zinc-300 bg-white'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={reason}
                      checked={selectedReason === reason}
                      onChange={(e) => handleReasonSelect(e.target.value)}
                      className="w-4 h-4 text-[#7d6633] focus:ring-[#7d6633] focus:ring-offset-0"
                    />
                    <span className={`text-sm ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                      {reason}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                  isDark
                    ? 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'
                    : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                }`}
              >
                Keep Subscription
              </button>
              <button
                onClick={handleNext}
                className="flex-1 py-3 px-4 rounded-lg font-semibold text-sm bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-6">
            {/* Confirmation Step */}
            <div className="mb-6">
              <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>
                One Last Step
              </h2>
              <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                Would you like to share any additional feedback? (Optional)
              </p>
            </div>

            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                Your selected reason:
              </label>
              <div className={`p-3 rounded-lg text-sm font-medium ${
                isDark ? 'bg-zinc-800 text-zinc-200' : 'bg-zinc-100 text-zinc-800'
              }`}>
                {selectedReason}
              </div>
            </div>

            <div className="mb-6">
              <label className={`block text-sm font-semibold mb-2 ${isDark ? 'text-zinc-200' : 'text-zinc-800'}`}>
                Additional feedback (optional):
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="Help us improve by sharing more details..."
                className={`w-full p-3 rounded-lg border-2 text-sm resize-none transition-colors ${
                  isDark
                    ? 'bg-zinc-800 border-white/10 text-zinc-200 placeholder-zinc-500 focus:border-[#7d6633]'
                    : 'bg-white border-zinc-200 text-zinc-900 placeholder-zinc-400 focus:border-[#7d6633]'
                } focus:outline-none focus:ring-2 focus:ring-[#7d6633]/20`}
              />
              <div className={`text-xs mt-1 text-right ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                {feedback.length}/500
              </div>
            </div>

            {/* Final Confirmation */}
            <div className={`p-4 rounded-lg mb-6 border-2 border-red-500/50 ${
              isDark ? 'bg-red-900/20' : 'bg-red-50'
            }`}>
              <p className={`text-sm font-medium ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                ⚠️ Are you absolutely sure you want to cancel your subscription?
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                disabled={loading}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                    ? 'bg-zinc-800 text-zinc-200 hover:bg-zinc-700'
                    : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                }`}
              >
                Back
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold text-sm transition-colors ${
                  loading
                    ? 'bg-zinc-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                } text-white`}
              >
                {loading ? 'Processing...' : 'Confirm Cancellation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribeModal;
